import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import cssVars from 'css-vars-ponyfill';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { datadogRum } from '@datadog/browser-rum';
import {
  LoaderProvider,
  LoaderThemeLight,
  ErrorProvider,
  InlineCommentingProvider,
} from '@pwc/pwc-deals-ui';
import { EventHubProvider } from '@pwc/event-hub-ui';
import { LiveReportPreviewContextProvider } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';

import { reactPlugin } from './app/ApplicationInsights';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { history, store } from './app/reduxStore';
import { eventHub } from './app/reduxMiddlewares';
import 'bootstrap/scss/bootstrap.scss';
import './styles/main.scss';
import '@pwc/pwc-deals-ui/build/styles/main.css';
import 'focus-visible/dist/focus-visible.min';
import jobRunner from './common/jobs/job.runner';
import './i18n';
import { ContextRoot } from './common/contexts/Root';
import { isInlineCommentingEnabled } from './common/helpers/featureFlags';

import 'urlpattern-polyfill';

jobRunner.start();

datadogRum.init({
  applicationId: '52a4f964-53ca-4edb-85f3-89df894937f8',
  clientToken: 'pubb147b4a7e27bf1c3b24469a7bdc8a156',
  site: 'us3.datadoghq.com',
  service: 'junction',
  useSecureSessionCookie: 'true',
  env: process.env.REACT_APP_ENVIRONMENT_NAMESPACE,
  version: '1.0.0',
  sampleRate: 100,
  trackInteractions: true,
  trackUserInteractions: true,
  trackFrustrations: true,
  defaultPrivacyLevel: 'mask-user-input',
  allowedTracingUrls: [
    'https://junction.us.pwc.com',
    'https://junction.eu.pwc.com',
    'https://junction.ap.pwc.com',
    'https://junction-us-dev.lower-pwclabs.pwcglb.com',
  ],
});

const cssPonyfill = () =>
  cssVars({
    onlyLegacy: true,
    include: 'style,link[rel="stylesheet"]:not([href*="//"])',
    // eslint-disable-next-line no-console
    onComplete: () => console.log('CSS Variables updated'),
  });

cssPonyfill();

export const bootStrap = ({ app: App, elementRoot }) =>
  // eslint-disable-next-line react/no-render-return-value
  ReactDOM.render(
    <ContextRoot.Provider value={{ bootStrap }}>
      <Provider store={store}>
        <AppInsightsContext.Provider value={reactPlugin}>
          <ConnectedRouter history={history}>
            <LoaderProvider
              config={{
                errorCodesMapping: { mappings: {}, codes: {} },
                themeConfig: { userThemes: { light: LoaderThemeLight } },
              }}
            >
              <ErrorProvider
                config={{
                  themeConfig: { userThemes: { light: LoaderThemeLight } },
                }}
                eventHub={eventHub}
              >
                <EventHubProvider eventHub={eventHub}>
                  <InlineCommentingProvider
                    isInlineCommentingEnabled={isInlineCommentingEnabled}
                  >
                    <LiveReportPreviewContextProvider value={{}}>
                      <App />
                    </LiveReportPreviewContextProvider>
                  </InlineCommentingProvider>
                </EventHubProvider>
              </ErrorProvider>
            </LoaderProvider>
          </ConnectedRouter>
        </AppInsightsContext.Provider>
      </Provider>
    </ContextRoot.Provider>,
    elementRoot
  );

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();
